import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Stack,
  TextField,
  MenuItem,
  Button,
  Chip,
  Typography,
  Tooltip,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
  type GridRenderCellParams,
} from "@mui/x-data-grid";
import { RefreshCw, Search, RotateCcw } from "lucide-react";
import axios from "axios";
import { axiosInstance } from "../../utility/apiInstance";
import { FOOTER_HEIGHT, HEADER_HEIGHT, PAGE_SIZE, ROW_ESTIMATE } from "../../components/Contants/constantValue";
import { CustomLoadingOverlay, CustomNoRowsOverlay } from "../../common/Datagridoverlays";
import toast from "react-hot-toast";


// ---- types ----
type TrackingSource =
  | "awb-subscribe"
  | "awb-track-hub"
  | "ocean-subscribe"
  | "ocean-track-hub";

interface FailedTrackingRow {
  _id: string;
  trackingNumber: string[] | string;
  customer?: string | null;
  organization?: string;
  error?: string | Record<string, unknown> | null;
  isErrorOccurred: boolean;
  source: TrackingSource;
  retryCount: number;
  maxRetries: number;
  createdAt: string;
  updatedAt: string;
  lastTriedAt?: string | null;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface FailedListResponse {
  statusCode: number;
  data: { items: FailedTrackingRow[]; pagination: Pagination };
  message: string;
  success: boolean;
}

interface RetryResponse {
  statusCode: number;
  data?: unknown;
  message?: string;
  success?: boolean;
}

type StatusFilter = "failed" | "exhausted" | "resolved" | "all";



const GRID_HEIGHT = HEADER_HEIGHT + FOOTER_HEIGHT + ROW_ESTIMATE * PAGE_SIZE;

const SOURCE_META: Record<TrackingSource, { label: string; type: string }> = {
  "awb-subscribe": { label: "Air", type: "MAWB" },
  "awb-track-hub": { label: "Air", type: "MAWB" },
  "ocean-subscribe": { label: "Ocean", type: "Ocean" },
  "ocean-track-hub": { label: "Ocean", type: "Ocean" },
};

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "failed", label: "Failed (retryable)" },
  { value: "exhausted", label: "Exhausted" }
];



const fmtDate = (v?: string | null): string =>
  v
    ? new Date(v).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
    : "—";

const asText = (v: unknown): string =>
  typeof v === "string" ? v : v == null ? "—" : JSON.stringify(v);

const trackingText = (row: FailedTrackingRow): string =>
  Array.isArray(row.trackingNumber)
    ? row.trackingNumber.join(", ")
    : row.trackingNumber || "—";

export default function FailedTrackingList(): React.ReactElement {
  const [rows, setRows] = useState<FailedTrackingRow[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);


  // filters
  const [search, setSearch] = useState<string>("");
  const [status, setStatus] = useState<StatusFilter>("failed");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");

  // server-side pagination (DataGrid page is 0-indexed, API is 1-indexed)
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });

  // guard so rapid filter/page changes don't race — only the latest request wins
  const requestSeq = useRef<number>(0);

  const fetchList = useCallback(async (): Promise<void> => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        page: paginationModel.page + 1, // API is 1-indexed
        limit: paginationModel.pageSize,
        status,
      };
      if (search.trim()) params.awb = search.trim().toUpperCase();
      if (fromDate) params.from = fromDate;
      if (toDate) params.to = toDate;

      const { data } = await axiosInstance.get<FailedListResponse>("api/v1/re-try/failed-list", { params });

      if (seq !== requestSeq.current) return; // a newer request superseded this one

      setRows(data?.data?.items ?? []);
      setRowCount(data?.data?.pagination?.total ?? 0);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setRows([]);
      setRowCount(0);
      const message =
        (axios.isAxiosError(err) && err.response?.data?.message) || "Failed to load records";
      toast.error(message);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [paginationModel, status, search, fromDate, toDate]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleRetry = async (id: string): Promise<void> => {
    setRetryingId(id);
    try {
      const { data } = await axiosInstance.post<RetryResponse>(`api/v1/re-try/${id}`);
      const ok = data?.statusCode === 200 || data?.success === true;
      fetchList()
    } catch (err) {
      const message = (axios.isAxiosError(err) && err.response?.data?.message) || "Retry failed";
      toast.error(message);
      fetchList();
    } finally {
      setRetryingId(null);
    }
  };

  const resetFilters = (): void => {
    setSearch("");
    setStatus("failed");
    setFromDate("");
    setToDate("");
    setPaginationModel({ page: 0, pageSize: PAGE_SIZE });
  };

  const onFilterChange =
    <T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
        setter(e.target.value as unknown as T);
        setPaginationModel((p) => ({ ...p, page: 0 }));
      };

  const columns: GridColDef<FailedTrackingRow>[] = [
    {
      field: "trackingNumber",
      headerName: "Tracking Number",
      flex: 1.2,
      minWidth: 180,
      sortable: false,
      valueGetter: (_value, row) => trackingText(row),
    },
    // {
    //   field: "shipment",
    //   headerName: "Shipment / Job No.",
    //   flex: 1.2,
    //   minWidth: 180,
    //   sortable: false,
    //   valueGetter: (_value, row) => trackingText(row),
    // },
    // {
    //   field: "mode",
    //   headerName: "Mode",
    //   width: 90,
    //   sortable: false,
    //   valueGetter: (_value, row) => SOURCE_META[row.source]?.label ?? "—",
    // },
    {
      field: "customerName",
      headerName: "Customer",
      flex: 1,
      minWidth: 150,
      sortable: false,
      valueGetter: (_value, row) => row.customer ?? "—",
    },
    {
      field: "error",
      headerName: "Failure Reason",
      flex: 1.6,
      minWidth: 220,
      sortable: false,
      renderCell: (params: GridRenderCellParams<FailedTrackingRow>) => (
        <Tooltip title={asText(params.value)}>
          <Typography variant="body2" noWrap>
            {asText(params.value)}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "lastTriedAt",
      headerName: "Failure Date & Time",
      width: 190,
      sortable: false,
      valueGetter: (_value, row) => row.lastTriedAt || row.createdAt,
      valueFormatter: (value) => fmtDate(value as string),
    },
    {
      field: "retryCount",
      headerName: "Retries",
      width: 100,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams<FailedTrackingRow>) => (
        <Typography variant="body2">
          {params.row.retryCount ?? 0}/{params.row.maxRetries ?? 5}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams<FailedTrackingRow>) => {
        const exhausted = (params.row.retryCount ?? 0) >= (params.row.maxRetries ?? 5);
        const failed = params.row.isErrorOccurred;
        if (!failed) return <Chip size="small" color="success" label="Success" />;
        return (
          <Chip
            size="small"
            color={exhausted ? "default" : "error"}
            label={exhausted ? "Exhausted" : "Failed"}
            variant={exhausted ? "outlined" : "filled"}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "Action",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<FailedTrackingRow>) => {
        const exhausted = (params.row.retryCount ?? 0) >= (params.row.maxRetries ?? 5);
        const resolved = !params.row.isErrorOccurred;
        const disabled = exhausted || resolved || retryingId === params.row._id;
        return (
          <Button
            size="small"
            variant="outlined"
            startIcon={
              retryingId === params.row._id ? <CircularProgress size={14} /> : <RotateCcw size={14} />
            }
            disabled={disabled}
            onClick={() => void handleRetry(params.row._id)}
          >
            Retry
          </Button>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Failed Tracking
        </Typography>
        <Button
          size="small"
          variant="text"
          startIcon={<RefreshCw size={16} />}
          onClick={() => fetchList()}
          disabled={loading}
        >
          Refresh
        </Button>
      </Stack>

      {/* Filters — stable container above the grid (search never loses focus) */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            alignItems: "end",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "minmax(220px, 1.6fr) minmax(150px, 1fr) minmax(150px, 1fr) minmax(150px, 1fr) auto",
            },
          }}
        >
          <TextField
            size="small"
            label="Search tracking no."
            placeholder="e.g. 020-28881924"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setPaginationModel((p) => ({ ...p, page: 0 }));
                fetchList();
              }
            }}
            InputProps={{
              startAdornment: (
                <Search size={16} style={{ marginRight: 8, color: "#9e9e9e", flexShrink: 0 }} />
              ),
            }}
            fullWidth
          />

          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={onFilterChange<StatusFilter>(setStatus)}
            fullWidth
          >
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={fromDate}
            onChange={onFilterChange<string>(setFromDate)}
            fullWidth
          />

          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={toDate}
            onChange={onFilterChange<string>(setToDate)}
            fullWidth
          />

          <Stack direction="row" spacing={1} sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}>
            <Button
              size="small"
              variant="outlined"
              onClick={resetFilters}
              sx={{ height: 40 }}
            >
              Reset
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ height: GRID_HEIGHT, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row._id}
          loading={loading}
          rowCount={rowCount}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20]}
          disableRowSelectionOnClick
          disableColumnMenu
          slots={{
            loadingOverlay: CustomLoadingOverlay,
            noRowsOverlay: CustomNoRowsOverlay,
          }}
          sx={{
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
          }}
        />
      </Paper>


    </Box>
  );
}